import type React from "react";

import type { QuestionDto } from "@/api/types";
import { FormInput } from "@/components/Form/FormInput";
import { FormStarInput } from "@/components/Form/FormStarInput";
import { useTranslation } from "@/translations";

import type { SurveyRespondFormValues } from "..";
import {
  SurveyRespondFormBinaryChoiceInput,
  SurveyRespondFormMultipleChoiceInput,
} from "./SurveyRespondFormMultipleChoiceInput";
import { SurveyRespondFormRankingInput } from "./SurveyRespondFormRankingInput";

interface SurveyRespondQuestionCardProps {
  question: QuestionDto;
}

export function SurveyRespondQuestionCard({ question }: SurveyRespondQuestionCardProps): React.ReactNode {
  const { t } = useTranslation();

  let helperLabel = "";
  if (question.type === "multipleChoice" || question.type === "yesNo") {
    helperLabel = t("page.survey-respond.form.label.multiple-choice");
  }

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg bg-white p-4">
      <h3 className="text-headline3">{question.question}</h3>
      {helperLabel && <p className="text-caption text-grey-700">{helperLabel}</p>}
      {question.type === "open" && (
        <FormInput<SurveyRespondFormValues, string>
          data-testid="survey-respond-open-question-input"
          name={`${question.id}.text`}
          placeholder={t("page.survey-respond.form.placeholder.open-question")}
          rules={{
            required: t("components.form.error.required-generic"),
          }}
        />
      )}
      {question.type === "rating" && (
        <FormStarInput<SurveyRespondFormValues, string>
          data-testid="survey-respond-rating-input"
          name={`${question.id}.rating`}
        />
      )}
      {question.type === "yesNo" && <SurveyRespondFormBinaryChoiceInput questionId={question.id} />}
      {question.type === "multipleChoice" && (
        <SurveyRespondFormMultipleChoiceInput questionId={question.id} offeredAnswers={question.answers || []} />
      )}
      {question.type === "ranking" && (
        <SurveyRespondFormRankingInput questionId={question.id} offeredAnswers={question.answers || []} />
      )}
    </div>
  );
}
